/**
 * 세무관리 — 기업등록 (고객사)
 * 세무법인(로그인 tenant) 스코프의 고객사 CRUD. 기관코드는 조회조건에 노출하지 않는다.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import {
  YN_OPTIONS,
  PROXY_INPUT_OPTIONS,
  emptyClient,
  loadClients,
  filterClients,
  persistClients,
  validateClients,
  nextSortOrderBy10,
  nextClientCode,
  createClientUid,
} from 'tax/clientCompanyStore';

const GRID_ID = 'tx-client-company';

const ClientCompanyInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const searchDefaultValues = {
    tenantCode,
    keyword: '',
    useYn: 'Y',
    proxyInputYn: '',
  };

  const methods = useForm({ mode: 'onSubmit', defaultValues: { ...searchDefaultValues } });
  const { getValues: getSearchValues, setValue: setSearchValue } = methods;

  useEffect(() => {
    setSearchValue('tenantCode', tenantCode);
  }, [tenantCode, setSearchValue]);

  const columns = useMemo(
    () => [
      hiCol.edTextColumnForKey({
        header: '고객사코드',
        field: 'clientCode',
        size: 130,
        required: true,
        title: '세무법인 내 고객사 식별코드. 저장 후 변경 불가',
      }),
      hiCol.edTextColumn({ header: '고객사명', field: 'clientName', size: 160, required: true }),
      hiCol.edTextColumn({ header: '사업자번호', field: 'bizNo', size: 130 }),
      hiCol.edTextColumn({ header: '대표자', field: 'ceoName', size: 90 }),
      hiCol.edTextColumn({ header: '업태', field: 'bizType', size: 90 }),
      hiCol.edTextColumn({ header: '종목', field: 'bizItem', size: 90 }),
      hiCol.edTextColumn({ header: '전화', field: 'tel', size: 110 }),
      hiCol.edTextColumn({ header: '이메일', field: 'email', size: 140 }),
      hiCol.edTextColumn({
        header: '그룹경로',
        field: 'clientGroupPath',
        size: 140,
        title: '예: /서울지점/제조업/ — 별도 트리 테이블 없이 경로 문자열로 그룹핑',
      }),
      hiCol.edTextColumn({ header: '관할세무서', field: 'taxOffice', size: 110 }),
      hiCol.edTextColumn({ header: '주소', field: 'address', size: 180 }),
      hiCol.edComboColumn({
        header: '입력주체',
        field: 'proxyInputYn',
        size: 100,
        options: PROXY_INPUT_OPTIONS,
        title: '여건이 안 되면 세무법인 직원이 동일 화면에서 대신 입력',
      }),
      hiCol.edCheckboxColumn({ header: '사용', field: 'useYn', size: 72 }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 72, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 140 }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues,
    enableContextMenu: true,
    autoSearch: false,
    confirmCheck: true,
    defaultRow: emptyClient({ useYn: 'Y', proxyInputYn: 'N', sortOrder: 10 }),
    getDefaultRow: () => {
      const data = gridRef.current?.getData?.() ?? [];
      return {
        clientUid: createClientUid(),
        clientCode: nextClientCode(data),
        sortOrder: nextSortOrderBy10(data),
      };
    },
    customOnSearch: async () => {
      const values = getSearchValues() || {};
      const all = loadClients(values.tenantCode || tenantCode);
      return filterClients(all, values);
    },
    customOnSave: async ({ setData }) => {
      const values = getSearchValues() || {};
      const current = gridRef.current?.getData?.() ?? [];
      const errors = validateClients(current);
      if (errors.length) {
        showAlert(errors.slice(0, 8).join('\n'), 'warning');
        return;
      }
      const next = persistClients(values.tenantCode || tenantCode, current, values);
      setData(next);
      showAlert('저장했습니다.', 'success');
    },
  });

  useEffect(() => {
    grid.handleSearch().catch((e) => showAlert(e.message || '조회 실패', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 기관 변경 시 재조회
  }, [tenantCode]);

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '기업등록',
    getData,
    getDataColumns,
    order: 0,
  });
  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '기업등록' });

  const actionButtons = [
    {
      id: 'search',
      label: '조회',
      icon: '🔍',
      onClick: () => grid.handleSearch().catch((e) => showAlert(e.message || '조회 실패', 'error')),
      hotkey: 'F7',
    },
    { id: 'add', label: '추가', icon: '➕', onClick: () => grid.handleAdd(), hotkey: 'F8' },
    { id: 'delete', label: '삭제', icon: '➖', onClick: () => grid.handleDelete(), hotkey: 'F9' },
    {
      id: 'save',
      label: '저장',
      icon: '💾',
      onClick: () => grid.handleSave().catch((e) => showAlert(e.message || '저장 실패', 'error')),
      hotkey: 'F10',
    },
    excelButton,
  ];

  const searchFields = [
    [
      {
        name: 'keyword',
        label: '검색어',
        width: CommonCss.w160,
        labelWidth: SEARCH_LABEL_W,
        placeholder: '코드·명·번호',
      },
      {
        name: 'proxyInputYn',
        label: '입력주체',
        type: 'select',
        width: CommonCss.w120,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '전체' }, ...PROXY_INPUT_OPTIONS],
      },
      {
        name: 'useYn',
        label: '사용',
        type: 'select',
        width: CommonCss.w80,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
      },
    ],
  ];

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel storageKey={GRID_ID} prefsUserId={prefsUserId} title="조회조건">
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={() => grid.handleSearch()}
            defaultValues={searchDefaultValues}
          />
        </CollapsibleSearchPanel>
      </FormProvider>
      <GridExportFocusScope gridId={GRID_ID}>
        <div style={{ flex: 1, minHeight: 0 }}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            enableColumnFilters
            enableContextMenu
            enableRowEditMenu
            enableCut
            enableFindReplace
            allowReplace
            enableHeaderMenu
            enableSelectionStats
            enableFreeze
            enableQuickExport
            showModColumn
            exportFileName="기업등록"
            gridId={GRID_ID}
            prefsUserId={prefsUserId}
            {...grid.contextMenuProps}
          />
        </div>
      </GridExportFocusScope>
      {excelDialog}
    </PageContainer>
  );
};

const ClientCompanyRegister = () => (
  <GridExportProvider>
    <ClientCompanyInner />
  </GridExportProvider>
);

export default ClientCompanyRegister;
